import { now } from "./CDate";

export function insertZeroWidthSpacesInParagraph(text, { chunkSize = 20, maxSize = 30 } = {}) {
  const regex = new RegExp(`(.{${chunkSize}})`, 'g');
  return text.split(/\b/).map(part => {
    // Process any continuous sequence of letters/numbers longer than 30 chars
    if (part.length > maxSize) {
      return part.replace(regex, '$1\u200B');
    }
    return part.replace(/([[\]_,/\-:()])/g, '$1\u200B');
  }).join('');
}

const base64Characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
export function base64Decode(data) {
    let result = [];
    let current = 0;

    let i, c;
    // eslint-disable-next-line no-cond-assign
    for (i = 0; c = data.charAt(i); i++) {
        if (c === '=') {
            if (i !== data.length - 1 && (i !== data.length - 2 || data.charAt(i + 1) !== '=')) {
                throw new SyntaxError('Unexpected padding character.');
            }
            break;
        }
        const index = base64Characters.indexOf(c);

        if (index === -1) {
            throw new SyntaxError('Invalid Base64 character.');
        }

        current = (current << 6) | index;

        if (i % 4 === 3) {
            result.push(current >> 16, (current & 0xff00) >> 8, current & 0xff);
            current = 0;
        }
    }

    if (i % 4 === 1) {
        throw new SyntaxError('Invalid length for a Base64 string.');
    }

    if (i % 4 === 2) {
        result.push(current >> 4);
    } else if (i % 4 === 3) {
        current <<= 6;
        result.push(current >> 16, (current & 0xff00) >> 8);
    }

    return result;
}

export function toInt32(bytes) {
    return (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
}
export function getPNGDimensions(data) {
    return {
        width: toInt32(data.slice(16, 20)),
        height: toInt32(data.slice(20, 24))
    };
}
export function jpegProps(data) {          // data is an array of bytes
    let off = 0;
    while (off < data.length) {
        while (data[off] === 0xff) off++;
        const mrkr = data[off]; off++;

        if (mrkr === 0xd8) continue;    // SOI
        if (mrkr === 0xd9) break;       // EOI
        if (0xd0 <= mrkr && mrkr <= 0xd7) continue;
        if (mrkr === 0x01) continue;    // TEM

        const len = (data[off] << 8) | data[off + 1]; off += 2;

        if (mrkr === 0xc0) return {
            bpc: data[off],     // precission (bits per channel)
            h: (data[off + 1] << 8) | data[off + 2],
            w: (data[off + 3] << 8) | data[off + 4],
            cps: data[off + 5]    // number of color components
        }
        off += len - 2;
    }
}

export const makeCell = (title, value, span, props) => {
    let toReturn = [{
        stack: [
            { text: title, style: 'formCellTitle' },
            { text: (value ? value : ' - '), alignment: 'left', style: 'data', margin: [15, 0, 0, 0], ...props}
        ]
    }];
    span = span ?? 12;
    if (span > 1) {
        toReturn[0].colSpan = span;
        for (let i = 1; i < span; ++i) {
            toReturn.push({});
        }
    }
    return toReturn;
}

export const makeTitle = function (title, span) {
    let toReturn = [{ text: title, alignment: 'left', style: 'bold', margin: [0, 10, 0, 0], border: [false, true, false, true] }];
    span = span ?? 12;
    if (span > 1) {
        toReturn[0].colSpan = span;
        for (let i = 1; i < span; ++i) {
            toReturn.push({});
        }
    }
    return toReturn;
}

export const makeImageCell = function (title, value, span) {
    let toReturn = [{
        stack: [
            { text: title, style: 'formCellTitle' },
        ]
    }];
    span = span ?? 12
    if (value) {
        toReturn[0].stack.push({ image: value, margin: [15, 0, 0, 0], fit: [((400 * span) / 12), 200] });
    } else {
        toReturn[0].stack.push({ text: ' ', alignment: 'left', style: 'data', margin: [15, 0, 0, 0] });
    }
    if (span > 1) {
        toReturn[0].colSpan = span;
        for (let i = 1; i < span; ++i) {
            toReturn.push({});
        }
    }
    return toReturn;
}

export const genDocDefinitionFactory = image => (configuration, content, username, options = {}) => {
    const docDefinition = {
        pageSize: 'A4',
        header: function (currentPage, pageCount) {
            return {
                columns: [
                    {
                        stack: image ? [
                            { image: image, width: 100 },
                            { text: configuration.eventName != null ? ('for ' + configuration.eventName) : '', style: 'small' }
                        ] : [
                            { text: configuration.eventName != null ? ('for ' + configuration.eventName) : '', style: 'small' }
                        ]
                    },
                    { text: (username ? username + '@' : '') + now(configuration.tz).dateTimeFormat(configuration.timezones.enable, true), alignment: 'right' }
                ],
                margin: [25, 5]
            };
        },
        footer: function (currentPage, pageCount) {
            return {
                columns: [
                    { text: 'www.chronosoft.com.au', style: 'small' },
                    { text: options?.footerText ?? '', style: 'small', alignment: 'center' },
                    { text: currentPage.toString() + ' of ' + pageCount, alignment: 'right', style: 'bold' }
                ],
                margin: [25, 10]
            };
        },
        defaultStyle: {
            fontSize: 10
        },
        content: content,
        styles: {
            header: {
                fontSize: 16,
                bold: true,
                margin: [0, 0, 0, 10]
            },
            subheader: {
                fontSize: 14,
                bold: true,
                margin: [0, 10, 0, 5]
            },
            bold: {
                bold: true
            },
            vitalsTableStyle: {
                fontSize: 8,
                margin: [0, 5, 0, 0]
            },
            red: {
                color: 'red'
            },
            noteStrike: {
                decoration: 'lineThrough'
            },
            tableHeader: {
                bold: true,
                fontSize: 12,
                color: 'black'
            },
            formCellTitle: {
                fontSize: 8,
                color: 'grey'
            },
            small: {
                fontSize: 8,
                color: 'gray'
            },
            data: {
                fontSize: 10
            },
            noteSys: {
                italics: true,
                color: 'grey'
            }
        }
    }
    return docDefinition;
}